import { useCallback, useEffect, useRef, useState } from "react";
import { uploadMediaFile } from "@/components/FileUpload";

export const VOICE_PREF_KEY = "yalola.prayer_voice"; // dernier choix « Prier à voix haute » (1 / 0)

export const voiceSupported = () => typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

export const readVoicePref = () => { try { return localStorage.getItem(VOICE_PREF_KEY) === "1"; } catch { return false; } };
export const writeVoicePref = (on) => { try { localStorage.setItem(VOICE_PREF_KEY, on ? "1" : "0"); } catch { /* noop */ } };

// Lot 657 — enregistre la voix pendant la minute de prière (MediaRecorder, comme les vocaux du chat).
export const usePrayerVoice = () => {
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const [recording, setRecording] = useState(false);

  const cleanup = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch { /* noop */ }
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    if (!voiceSupported()) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start(1000);
      streamRef.current = stream;
      recRef.current = rec;
      startedRef.current = Date.now();
      setRecording(true);
      return true;
    } catch { cleanup(); return false; }
  }, [cleanup]);

  // Arrête l'enregistrement → { blob, duration } (null si rien d'exploitable).
  const stop = useCallback(() => new Promise((resolve) => {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") { cleanup(); resolve(null); return; }
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      const duration = Math.min(60, Math.max(0, Math.round((Date.now() - startedRef.current) / 1000)));
      cleanup();
      resolve(blob.size > 1000 ? { blob, duration } : null);
    };
    try { if (rec.state === "paused") rec.resume(); rec.stop(); } catch { cleanup(); resolve(null); }
  }), [cleanup]);

  const discard = useCallback(() => {
    const rec = recRef.current;
    try { if (rec && rec.state !== "inactive") { rec.onstop = null; rec.stop(); } } catch { /* noop */ }
    cleanup();
  }, [cleanup]);

  useEffect(() => discard, [discard]);
  return { recording, start, stop, discard };
};

// Envoie la prière vocale comme un vocal du chat → { url, silent } (null si échec).
// Lot 682 — le serveur convertit le WebM/Opus en M4A (lisible sur iPhone) et mesure le volume : `silent` = micro non capté.
export const uploadPrayerVoice = async (blob, config) => {
  const file = new File([blob], "priere.webm", { type: blob.type || "audio/webm" });
  const data = await uploadMediaFile(file, { config, context: "prayer" });
  if (!data?.url) return null;
  return { url: data.url, silent: !!data.audio_meta?.silent, duration: data.audio_meta?.duration || data.duration || 0 };
};
